import { useTranslation } from "react-i18next";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import "scorm-again/dist/scorm12.js";
import "scorm-again/dist/scorm2004.js";

const Scorm = (props: any) => {
  const { t } = useTranslation();
	const [directLink, setDirectLink] = useState<string>("");
	const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
	const [isButtonHovered, setIsButtonHovered] = useState<boolean>(false);
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	let src = props.src || '';

	useEffect(() => {
		const fetchData = async () => {
			try {
				if (!src) return;

				let targetUrl = src;
				if (src.includes("/open-api/cdn")) {
					const sep = src.includes("?") ? "&" : "?";
					targetUrl = `${src}${sep}redirect=false`;
				}

				const response = await axios.get(targetUrl);
				const rawLink =
					response?.data?.url ||
					response?.data?.data?.url ||
					response?.request?.responseURL;

				if (rawLink) {
					const url = new URL(rawLink);
					const modifiedPath = `/s3${url.pathname}`;
					setDirectLink(modifiedPath);
				}
			} catch (error) {
				console.error("Error fetching data:", error);
			}
		};

		fetchData();
	}, [src]);

	useEffect(() => {
		const iframe = iframeRef.current;

		if (iframe && directLink) {
			iframe.src = 'about:blank';

			const onLoadHandler = () => {
				const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;

				if (iframeDocument) {
					const settings = { autocommit: true };

					// @ts-ignore - Create SCORM 1.2 API
					const api12 = new Scorm12API(settings);
					// @ts-ignore - Create SCORM 2004 API
					const api2004 = new Scorm2004API(settings);

					// Assign to window for SCORM content to find
					(window as any).API = api12;             // SCORM 1.2
					(window as any).API_1484_11 = api2004;   // SCORM 2004

					// Add LMS* method aliases for SCORM 1.2 compatibility
					if (!api12.LMSInitialize) api12.LMSInitialize = api12.Initialize;
					if (!api12.LMSFinish) api12.LMSFinish = api12.Terminate;
					if (!api12.LMSGetValue) api12.LMSGetValue = api12.GetValue;
					if (!api12.LMSSetValue) api12.LMSSetValue = api12.SetValue;
					if (!api12.LMSCommit) api12.LMSCommit = api12.Commit;
					if (!api12.LMSGetLastError) api12.LMSGetLastError = api12.GetLastError;
					if (!api12.LMSGetErrorString) api12.LMSGetErrorString = api12.GetErrorString;
					if (!api12.LMSGetDiagnostic) api12.LMSGetDiagnostic = api12.GetDiagnostic;

					iframe.src = directLink;
				}

				iframe.removeEventListener('load', onLoadHandler);
			};

			iframe.addEventListener('load', onLoadHandler);
		}
	}, [directLink]);

	// Handle fullscreen toggle
	const toggleFullscreen = () => {
		const container = containerRef.current;
		if (!container) return;

		if (!isFullscreen) {
			// Enter fullscreen
			if (container.requestFullscreen) {
				container.requestFullscreen();
			} else if ((container as any).webkitRequestFullscreen) {
				(container as any).webkitRequestFullscreen();
			} else if ((container as any).mozRequestFullScreen) {
				(container as any).mozRequestFullScreen();
			} else if ((container as any).msRequestFullscreen) {
				(container as any).msRequestFullscreen();
			}
		} else {
			// Exit fullscreen
			if (document.exitFullscreen) {
				document.exitFullscreen();
			} else if ((document as any).webkitExitFullscreen) {
				(document as any).webkitExitFullscreen();
			} else if ((document as any).mozCancelFullScreen) {
				(document as any).mozCancelFullScreen();
			} else if ((document as any).msExitFullscreen) {
				(document as any).msExitFullscreen();
			}
		}
	};

	// Listen to fullscreen changes
	useEffect(() => {
		const handleFullscreenChange = () => {
			const isCurrentlyFullscreen = !!(
				document.fullscreenElement ||
				(document as any).webkitFullscreenElement ||
				(document as any).mozFullScreenElement ||
				(document as any).msFullscreenElement
			);
			setIsFullscreen(isCurrentlyFullscreen);
		};

		document.addEventListener('fullscreenchange', handleFullscreenChange);
		document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
		document.addEventListener('mozfullscreenchange', handleFullscreenChange);
		document.addEventListener('MSFullscreenChange', handleFullscreenChange);

		return () => {
			document.removeEventListener('fullscreenchange', handleFullscreenChange);
			document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
			document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
			document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
		};
	}, []);

	return (
		<div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
			{/* Compact Fullscreen Button */}
			<button
				onClick={toggleFullscreen}
				onMouseEnter={() => setIsButtonHovered(true)}
				onMouseLeave={() => setIsButtonHovered(false)}
				style={{
					position: 'absolute',
					top: '10px',
					right: '10px',
					zIndex: 1000,
					padding: isButtonHovered ? '10px 14px' : '10px',
					backgroundColor: 'rgba(0, 0, 0, 0.6)',
					color: '#fff',
					border: 'none',
					borderRadius: '8px',
					cursor: 'pointer',
					fontSize: '14px',
					fontWeight: '500',
					transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
					opacity: isButtonHovered ? 1 : 0.4,
					boxShadow: isButtonHovered
						? '0 4px 12px rgba(0, 0, 0, 0.25)'
						: '0 2px 8px rgba(0, 0, 0, 0.15)',
					display: 'flex',
					alignItems: 'center',
					gap: '8px',
					overflow: 'hidden',
					whiteSpace: 'nowrap',
					width: isButtonHovered ? 'auto' : '36px',
					height: '36px',
				}}
			>
				{isFullscreen ? (
					<>
						{/* Exit Fullscreen Icon */}
						<svg
							width="16"
							height="16"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							style={{ flexShrink: 0 }}
						>
							<path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
						</svg>
						<span style={{
							opacity: isButtonHovered ? 1 : 0,
							transition: 'opacity 0.3s ease',
							marginLeft: isButtonHovered ? '0' : '-100px',
						}}>
							{t("Thoát toàn màn hình", "Thoát toàn màn hình")}
						</span>
					</>
				) : (
					<>
						{/* Enter Fullscreen Icon */}
						<svg
							width="16"
							height="16"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							style={{ flexShrink: 0 }}
						>
							<path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
						</svg>
						<span style={{
							opacity: isButtonHovered ? 1 : 0,
							transition: 'opacity 0.3s ease',
							marginLeft: isButtonHovered ? '0' : '-100px',
						}}>
							{t("Xem toàn màn hình", "Xem toàn màn hình")}
						</span>
					</>
				)}
			</button>

			<iframe
				ref={iframeRef}
				src=""
				allow="fullscreen"
				scrolling="yes"
				width="100%"
				height={isFullscreen ? '100vh' : '900px'}
				style={{
					border: 'none',
					display: 'block',
				}}
			/>
			<br />
		</div>
	);
};

export default Scorm;
